export function Terms() {
  return (
    <div className="legal-page">
      <div className="container">
        <h1>Terms of Service</h1>
        <p className="legal-updated">Last updated: February 23, 2026</p>

        <div className="legal-content">
          <h2>1. Introduction</h2>
          <p>
            Welcome to CryptoGuardians, a non-custodial wallet intelligence and smart contract risk
            analysis platform. By accessing or using CryptoGuardians, you agree to be bound by these
            Terms of Service (&ldquo;Terms&rdquo;). If you do not agree, do not use the service.
          </p>

          <h2>2. Operator Disclosure</h2>
          <p>
            CryptoGuardians is operated by HAJ Solutions, Inc., a Texas corporation
            (&ldquo;Company,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;).
          </p>

          <h2>3. Nature of Service</h2>
          <p>
            CryptoGuardians is a <strong>software tool</strong> and{' '}
            <strong>informational analysis platform</strong>. It provides automated risk indicators
            and transaction analysis to help users evaluate smart contract interactions.
            CryptoGuardians does not:
          </p>
          <ul>
            <li>Take custody of any digital assets or private keys</li>
            <li>Execute, approve, or submit transactions on your behalf</li>
            <li>Reverse, freeze, or recover any blockchain transactions</li>
            <li>Act as a financial institution, bank, or money transmitter</li>
            <li>Provide personalized recommendations based on your financial situation</li>
          </ul>

          <h2>4. No Financial, Investment, Legal, or Tax Advice</h2>
          <p>
            <strong>
              CryptoGuardians does not provide financial, investment, legal, or tax advice.
            </strong>{' '}
            All analysis, risk scores, threat assessments, and alerts provided by CryptoGuardians
            are informational only and are generated by automated software algorithms. They do not
            constitute a recommendation to buy, sell, hold, or transact in any digital asset.
          </p>
          <p>
            You are solely responsible for your own transaction decisions. You should consult with
            qualified financial, legal, and tax professionals before making any investment or
            transaction decisions. Always conduct your own independent research before engaging in
            cryptocurrency transactions.
          </p>

          <h2>5. No Guarantee of Detection</h2>
          <p>
            <strong>
              CryptoGuardians does not guarantee the detection of all scams, threats, exploits,
              vulnerabilities, or malicious smart contracts.
            </strong>{' '}
            The blockchain ecosystem evolves rapidly, and new attack vectors, zero-day exploits, and
            novel scam patterns may emerge that are not yet covered by our analysis. No security
            tool can provide absolute protection. Risk scores and assessments represent the
            platform&rsquo;s best automated analysis at a point in time and may be incomplete or
            inaccurate.
          </p>

          <h2>6. No Fiduciary Relationship</h2>
          <p>
            <strong>
              Your use of CryptoGuardians does not create a fiduciary relationship between you and
              HAJ Solutions, Inc.
            </strong>{' '}
            We are not your financial advisor, investment advisor, broker, dealer, or fiduciary in
            any capacity. We owe you no fiduciary duty and assume no fiduciary obligations to you.
            We do not act in your best interest in any advisory capacity.
          </p>

          <h2>7. No Professional Relationship</h2>
          <p>
            <strong>
              CryptoGuardians is not your broker, financial advisor, investment advisor, fiduciary,
              agent, partner, or representative.
            </strong>{' '}
            Your use of the Service does not create any advisory, fiduciary, professional, or
            agency relationship between you and CryptoGuardians or HAJ Solutions, Inc. No
            communication from CryptoGuardians, whether through the platform, email, support
            channels, or any other medium, shall be construed as creating any such relationship.
          </p>

          <h2>8. Service Provided &ldquo;As-Is&rdquo; &mdash; Disclaimer of Warranties</h2>
          <p>
            <strong>
              CryptoGuardians is provided on an &ldquo;AS-IS&rdquo; and &ldquo;AS-AVAILABLE&rdquo;
              basis without warranties of any kind, either express or implied.
            </strong>{' '}
            To the maximum extent permitted by applicable law, HAJ Solutions, Inc. expressly
            disclaims all warranties, including but not limited to:
          </p>
          <ul>
            <li>
              Implied warranties of merchantability, fitness for a particular purpose, and
              non-infringement
            </li>
            <li>
              Warranties regarding the accuracy, reliability, or completeness of risk scores,
              threat assessments, or analysis results
            </li>
            <li>
              Warranties that the service will be uninterrupted, error-free, secure, or free of
              viruses or harmful components
            </li>
            <li>
              Warranties that analysis results will detect all threats or prevent all losses
            </li>
          </ul>

          <h2>9. No Warranty of Accuracy</h2>
          <p>
            <strong>
              CryptoGuardians does not warrant the accuracy, completeness, timeliness, or
              reliability of any analysis, risk score, threat assessment, or information provided
              through the Service.
            </strong>{' '}
            All outputs are generated by automated algorithms using data available at the time of
            analysis. Data sources may be incomplete, delayed, or contain errors. Risk scores are
            probabilistic estimates and are not definitive determinations of safety or risk. You
            acknowledge that no automated analysis system is infallible and that you must
            independently verify all information before acting on it.
          </p>

          <h2>10. User Responsibility</h2>
          <p>
            <strong>
              You are solely and entirely responsible for all blockchain transactions you initiate,
              approve, or execute.
            </strong>{' '}
            CryptoGuardians provides informational analysis to assist your decision-making, but the
            final decision to approve or reject any transaction rests entirely with you. By using
            CryptoGuardians, you acknowledge and accept that:
          </p>
          <ul>
            <li>You assume all risk associated with your blockchain transactions</li>
            <li>
              You will not rely solely on CryptoGuardians analysis when making transaction decisions
            </li>
            <li>
              CryptoGuardians is a supplementary tool and not a substitute for your own due
              diligence
            </li>
            <li>
              You understand the inherent risks of cryptocurrency and decentralized finance
            </li>
          </ul>

          <h2>11. User Makes Independent Decisions</h2>
          <p>
            <strong>
              You are solely responsible for all blockchain transactions and decisions you make.
            </strong>{' '}
            CryptoGuardians provides informational analysis only. All decisions to initiate,
            approve, reject, or execute any blockchain transaction are made independently by you.
            You must independently verify all information provided by CryptoGuardians before
            relying on it. CryptoGuardians does not direct, instruct, or advise you to take or
            refrain from taking any specific action regarding any transaction, token, protocol, or
            digital asset.
          </p>

          <h2>12. Blockchain Risk Disclosure</h2>
          <p>
            Cryptocurrency and blockchain transactions carry inherent risks, including but not
            limited to:
          </p>
          <ul>
            <li>Irreversible transactions once confirmed on-chain</li>
            <li>Smart contract vulnerabilities and exploits</li>
            <li>Market volatility and potential total loss of funds</li>
            <li>Regulatory changes that may affect the legality or value of digital assets</li>
            <li>
              Network congestion, forks, or protocol changes that may affect transaction execution
            </li>
            <li>Phishing attacks, social engineering, and other security threats</li>
          </ul>
          <p>
            CryptoGuardians cannot freeze, recover, or reverse any blockchain transaction.
          </p>

          <h2>13. Third-Party Services Disclaimer</h2>
          <p>
            <strong>
              CryptoGuardians relies on third-party data providers, blockchain infrastructure
              services, and external APIs to deliver its analysis.
            </strong>{' '}
            CryptoGuardians is not responsible for and makes no representations or warranties
            regarding:
          </p>
          <ul>
            <li>
              The accuracy, completeness, or reliability of data provided by third-party services
            </li>
            <li>
              Any downtime, outage, or unavailability of third-party services that may affect
              CryptoGuardians functionality
            </li>
            <li>
              Any errors, failures, or interruptions in third-party services that may result in
              incomplete or inaccurate analysis
            </li>
            <li>
              The security practices or privacy policies of third-party service providers
            </li>
          </ul>
          <p>
            CryptoGuardians shall not be liable for any losses, damages, or claims arising from the
            failure, inaccuracy, or unavailability of any third-party service on which
            CryptoGuardians relies.
          </p>

          <h2>14. Service Availability</h2>
          <p>
            <strong>
              CryptoGuardians may be interrupted, unavailable, or produce inaccurate results at
              any time without notice.
            </strong>{' '}
            CryptoGuardians makes no guarantee of uninterrupted, continuous, or error-free service.
            The Service may be subject to scheduled or unscheduled maintenance, updates, upgrades,
            or outages. We shall not be liable for any losses or damages arising from any
            interruption, suspension, or unavailability of the Service, regardless of cause or
            duration.
          </p>

          <h2>15. Subscription &amp; Billing</h2>
          <ul>
            <li>Paid plans are billed on a monthly recurring basis via Stripe.</li>
            <li>
              Your subscription automatically renews unless cancelled before the next billing date.
            </li>
            <li>Cancellation stops future billing but does not trigger an automatic refund.</li>
            <li>
              Refunds are governed exclusively by our{' '}
              <a href="/refund-policy">Refund Policy</a>.
            </li>
          </ul>

          <h2>16. Refund Policy Reference</h2>
          <p>
            Refund eligibility is governed by our <a href="/refund-policy">Refund Policy</a>.
            Please review the Refund Policy for complete details regarding eligibility, process,
            and limitations.
          </p>

          <h2>17. Prohibited Conduct</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Reverse engineer, decompile, or disassemble any part of the service</li>
            <li>Abuse the refund process or engage in serial refund attempts</li>
            <li>Create multiple accounts to bypass subscription limits or restrictions</li>
            <li>
              Use automated tools, bots, or scripts to access or interact with the service without
              authorization
            </li>
            <li>
              Initiate fraudulent chargebacks or payment disputes without first contacting support
            </li>
            <li>Resell, sublicense, or redistribute the service without written permission</li>
          </ul>

          <h2>18. Account Termination</h2>
          <p>
            We reserve the right to suspend or terminate your access to CryptoGuardians at any time
            for fraud, abuse, or violation of these Terms. Upon termination, your right to use the
            service ceases immediately. Refunds for terminated accounts are provided only as
            described in our <a href="/refund-policy">Refund Policy</a>.
          </p>

          <h2>19. Limitation of Liability</h2>
          <p>
            <strong>
              To the maximum extent permitted by applicable law, HAJ Solutions, Inc. and its
              officers, directors, employees, agents, affiliates, and licensors shall not be liable
              for any indirect, incidental, special, consequential, or punitive damages,
            </strong>{' '}
            including but not limited to:
          </p>
          <ul>
            <li>Indirect damages of any kind</li>
            <li>Consequential damages of any kind</li>
            <li>Incidental damages of any kind</li>
            <li>Special damages of any kind</li>
            <li>Punitive or exemplary damages</li>
            <li>Lost profits or lost revenue</li>
            <li>Lost cryptocurrency, tokens, or digital assets</li>
            <li>Lost opportunity or lost anticipated savings</li>
            <li>Loss of data or business interruption</li>
            <li>
              Damages arising from undetected exploits, scams, or smart contract vulnerabilities
            </li>
            <li>Market losses or losses due to transaction timing</li>
            <li>Damages arising from unauthorized access to your accounts or wallets</li>
          </ul>
          <p>
            <strong>
              CryptoGuardians shall not be liable for any cryptocurrency losses under any
              circumstances, including but not limited to losses resulting from transactions you
              approved, executed, or failed to execute, regardless of whether CryptoGuardians
              analysis was consulted, available, accurate, or inaccurate at the time.
            </strong>
          </p>
          <p>
            <strong>
              Our total aggregate liability for any and all claims arising from or related to these
              Terms or the service shall not exceed the total amount paid by you to CryptoGuardians
              during the twelve (12) months immediately preceding the event giving rise to the
              claim.
            </strong>
          </p>

          <h2>20. Indemnification</h2>
          <p>
            <strong>
              You agree to indemnify, defend, and hold harmless HAJ Solutions, Inc., its officers,
              directors, employees, agents, affiliates, and licensors
            </strong>{' '}
            from and against any and all claims, damages, losses, liabilities, costs, and expenses
            (including reasonable attorneys&rsquo; fees and court costs) arising out of or related
            to:
          </p>
          <ul>
            <li>Your use of or inability to use CryptoGuardians</li>
            <li>Your blockchain transactions, regardless of whether analysis was provided</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any applicable law or regulation</li>
            <li>Any content or information you provide through the service</li>
          </ul>

          <h2>21. Dispute Resolution &mdash; Binding Arbitration and Class Action Waiver</h2>
          <p>
            <strong>
              Any dispute, claim, or controversy arising from or relating to these Terms, the
              Service, or your use of CryptoGuardians shall be resolved exclusively through
              binding arbitration.
            </strong>{' '}
            Arbitration shall be conducted in Montgomery County, Texas, in accordance with the
            rules of the American Arbitration Association. The arbitrator&rsquo;s decision shall be
            final and binding and may be entered as a judgment in any court of competent
            jurisdiction.
          </p>
          <p>
            <strong>
              You agree to waive any right to participate in a class action lawsuit, class-wide
              arbitration, or any other representative proceeding to the fullest extent permitted
              by law.
            </strong>{' '}
            All disputes shall be resolved on an individual basis only. You agree that you may
            bring claims against CryptoGuardians or HAJ Solutions, Inc. only in your individual
            capacity and not as a plaintiff or class member in any purported class, collective, or
            representative proceeding.
          </p>

          <h2>22. Governing Law and Venue</h2>
          <p>
            <strong>
              These Terms shall be governed exclusively by and construed in accordance with the laws
              of the State of Texas, United States, without regard to conflict of law provisions.
            </strong>{' '}
            Any legal action or proceeding not subject to arbitration under Section 21 shall be
            brought exclusively in the state or federal courts located in Montgomery County, Texas.
            You consent to the exclusive jurisdiction and venue of such courts and waive any
            objection based on inconvenient forum.
          </p>

          <h2>23. Severability</h2>
          <p>
            If any provision of these Terms is found to be unenforceable or invalid by a court of
            competent jurisdiction, that provision shall be limited or eliminated to the minimum
            extent necessary, and the remaining provisions shall remain in full force and effect.
          </p>

          <h2>24. Entire Agreement</h2>
          <p>
            These Terms, together with the <a href="/privacy">Privacy Policy</a>,{' '}
            <a href="/disclaimer">Risk Disclaimer</a>, and{' '}
            <a href="/refund-policy">Refund Policy</a>, constitute the entire agreement between you
            and HAJ Solutions, Inc. regarding CryptoGuardians and supersede all prior agreements and
            understandings.
          </p>

          <h2>25. Changes to These Terms</h2>
          <p>
            We may modify these Terms at any time. Material changes will be posted on this page with
            an updated effective date. Continued use of CryptoGuardians after changes constitutes
            acceptance of the revised Terms.
          </p>

          <h2>26. Contact</h2>
          <p>
            Questions about these Terms? Contact us at{' '}
            <a href="mailto:legal@cryptoguardians.io">legal@cryptoguardians.io</a>.
          </p>

          <p className="legal-updated" style={{ marginTop: '3rem', borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem' }}>
            CryptoGuardians is a product of HAJ Solutions Inc.
          </p>
        </div>
      </div>
    </div>
  );
}
